import { useMemo, useState } from "react";
import { Card, DatePicker, Empty, Select, Skeleton } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import keyBy from "lodash.keyby";

import { teamSelectors } from "@fitness-app/app-store";
import { TeamMemberRole, TeamMemberStatus } from "@fitness-app/data-models/entities/TeamMember";

import { useAppSelector } from "~/store/initializeStore";
import { ConversationsHeatmap } from "./components/ConversationsHeatmap";
import { ResponseTimeDistributionChart } from "./components/ResponseTimeDistributionChart";
import { ResponseTimeKPIs } from "./components/ResponseTimeKPIs";
import { TrainerBreachRateChart } from "./components/TrainerBreachRateChart";
import { TrainerResponseTimeChart } from "./components/TrainerResponseTimeChart";
import { useChatResponseMetrics } from "./hooks/useChatResponseMetrics";

const monthFormat = "YYYY/MM";

export const ALL_TRAINERS_FILTER_ID = "all";

export default function ChatResponseMetrics() {
  const [selectedMonth, setMonth] = useState<Dayjs>(dayjs());
  const [selectedTrainers, setSelectedTrainers] = useState<string[]>([ALL_TRAINERS_FILTER_ID]);
  const trainers = useAppSelector(teamSelectors.getTrainersTeam) ?? [];

  const activeTrainers = useMemo(
    () =>
      trainers.filter(
        (trainer) =>
          trainer.selectable &&
          trainer.status === TeamMemberStatus.ACTIVE &&
          (trainer.role === TeamMemberRole.REGULAR_TRAINER || trainer.role === TeamMemberRole.SHARED_TRAINER),
      ),
    [trainers],
  );

  const activeTrainerIds = useMemo(() => activeTrainers.map((trainer) => trainer.id), [activeTrainers]);

  const { data, isLoading } = useChatResponseMetrics({
    month: selectedMonth,
    trainerIds: selectedTrainers.includes(ALL_TRAINERS_FILTER_ID) ? activeTrainerIds : selectedTrainers,
  });

  const handleChange = (value: string[]) => {
    if (value.length === 0) {
      setSelectedTrainers([ALL_TRAINERS_FILTER_ID]);
      return;
    }

    if (value.includes(ALL_TRAINERS_FILTER_ID) && value.length > 1) {
      const filtered = value.filter((item) => item !== ALL_TRAINERS_FILTER_ID);
      setSelectedTrainers(filtered);
    } else {
      setSelectedTrainers(value);
    }
  };

  const trainersList = useMemo(() => {
    const list = activeTrainers.map((trainer) => ({
      label: `${trainer.user?.firstName} ${trainer.user?.lastName}`,
      value: trainer.id,
    }));

    return [{ label: "Wszyscy trenerzy", value: ALL_TRAINERS_FILTER_ID }, ...list];
  }, [activeTrainers]);

  const trainersById = useMemo(() => keyBy(activeTrainers, "id"), [activeTrainers]);

  return (
    <div className="space-y-6">
      <Card
        title="Metryki czasu odpowiedzi na wiadomości"
        extra={
          <div className="flex justify-end gap-3">
            <Select
              mode="multiple"
              value={selectedTrainers}
              onChange={handleChange}
              style={{ minWidth: 320, maxWidth: 500 }}
              options={trainersList}
              placeholder="Wybierz trenerów"
            />
            <DatePicker
              value={selectedMonth}
              format={monthFormat}
              picker="month"
              onChange={(date) => date && setMonth(date)}
              disabledDate={(p) => p.isAfter(dayjs()) || p.isBefore(dayjs("2025-12-01"))}
            />
          </div>
        }
      >
        <Skeleton active loading={isLoading} paragraph={{ rows: 4 }}>
          {!data || data.byTrainer.length === 0 ? (
            <Empty description="Brak danych dla wybranego okresu" />
          ) : (
            <div className="space-y-6">
              <ResponseTimeKPIs data={data} isLoading={isLoading} />

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <ConversationsHeatmap data={data.heatmap} />
                <ResponseTimeDistributionChart
                  data={data.responseTimeDistribution}
                  avgBusinessMinutes={data.totals.avgBusinessMinutes}
                />
              </div>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <TrainerResponseTimeChart data={data.byTrainer} trainersById={trainersById} />
                <TrainerBreachRateChart data={data.byTrainer} trainersById={trainersById} />
              </div>
            </div>
          )}
        </Skeleton>
      </Card>
    </div>
  );
}
